import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Grid,
  FormControl,
  FormControlLabel,
  Radio,
  Checkbox,
  MenuItem,
  FormLabel,
  RadioGroup,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Select from './Select';
import TextField from './TextField';
import Card from './Card';
import Button from './Button';

const TestEditor = ({ test, testIndex, onChange, onRemove }) => {
  const { t } = useTranslation(['tests', 'common']);

  const handleTestChange = (e) => {
    const { name, value } = e.target;
    const processedValue = ['timeLimit', 'passingScore'].includes(name)
      ? Number(value)
      : value;
    onChange({
      ...test,
      [name]: processedValue,
    });
  };

  const addQuestion = () => {
    onChange({
      ...test,
      questions: [
        ...(test.questions || []),
        {
          type: 'single-choice',
          question: '',
          options: [
            { text: '', isCorrect: true },
            { text: '', isCorrect: false },
            { text: '', isCorrect: false },
            { text: '', isCorrect: false },
          ],
          points: 1,
        },
      ],
    });
  };

  const removeQuestion = (questionIndex) => {
    const updatedQuestions = test.questions.filter(
      (_, i) => i !== questionIndex
    );
    onChange({
      ...test,
      questions: updatedQuestions,
    });
  };

  const handleQuestionChange = (questionIndex, e) => {
    const { name, value } = e.target;
    const updatedQuestions = [...test.questions];
    const processedValue = name === 'points' ? Math.max(1, Number(value) || 1) : value;

    updatedQuestions[questionIndex] = {
      ...updatedQuestions[questionIndex],
      [name]: processedValue,
    };

    onChange({
      ...test,
      questions: updatedQuestions,
    });
  };

  const handleQuestionTypeChange = (questionIndex, e) => {
    const { value } = e.target;
    const updatedQuestions = [...test.questions];
    const currentQuestion = updatedQuestions[questionIndex];

    // Обновляем тип вопроса
    currentQuestion.type = value;

    // Для разных типов вопросов устанавливаем разные шаблоны ответов
    if (value === 'single-choice' || value === 'multiple-choice') {
      currentQuestion.options = [
        { text: '', isCorrect: true },
        { text: '', isCorrect: false },
        { text: '', isCorrect: false },
        { text: '', isCorrect: false },
      ];
    } else if (value === 'memory') {
      currentQuestion.options = [
        {
          text: t('tests:editor.cardPlaceholder', { number: 1 }),
          isCorrect: true,
          pairId: 'pair1',
        },
        {
          text: t('tests:editor.cardPlaceholder', {
            number: '1 (pair)',
          }),
          isCorrect: true,
          pairId: 'pair1',
        },
        {
          text: t('tests:editor.cardPlaceholder', { number: 2 }),
          isCorrect: true,
          pairId: 'pair2',
        },
        {
          text: t('tests:editor.cardPlaceholder', {
            number: '2 (pair)',
          }),
          isCorrect: true,
          pairId: 'pair2',
        },
      ];
    } else if (value === 'true-false') {
      currentQuestion.options = [];
      currentQuestion.correctAnswer = 'true';
    } else if (value === 'text') {
      currentQuestion.options = [];
      currentQuestion.correctAnswer = '';
    } else if (value === 'open-answer') {
      currentQuestion.options = [];
      currentQuestion.correctAnswer = '';
    }

    onChange({
      ...test,
      questions: updatedQuestions,
    });
  };

  const addOption = (questionIndex) => {
    const updatedQuestions = [...test.questions];
    const questionType = updatedQuestions[questionIndex].type;

    if (questionType === 'memory') {
      const options = updatedQuestions[questionIndex].options;
      const pairCount = Math.floor(options.length / 2) + 1;
      const newPairId = `pair${pairCount}`;

      updatedQuestions[questionIndex].options.push(
        {
          text: t('tests:editor.cardPlaceholder', {
            number: pairCount,
          }),
          isCorrect: true,
          pairId: newPairId,
        },
        {
          text: t('tests:editor.cardPlaceholder', {
            number: `${pairCount} (pair)`,
          }),
          isCorrect: true,
          pairId: newPairId,
        }
      );
    } else {
      updatedQuestions[questionIndex].options.push({
        text: '',
        isCorrect: false,
      });
    }

    onChange({
      ...test,
      questions: updatedQuestions,
    });
  };

  const removeOption = (questionIndex, optionIndex) => {
    const updatedQuestions = [...test.questions];
    updatedQuestions[questionIndex].options.splice(optionIndex, 1);
    onChange({
      ...test,
      questions: updatedQuestions,
    });
  };

  const handleOptionChange = (questionIndex, optionIndex, e) => {
    const { value } = e.target;
    const updatedQuestions = [...test.questions];
    const questionType = updatedQuestions[questionIndex].type;

    if (questionType === 'memory') {
      updatedQuestions[questionIndex].options[optionIndex] = {
        ...updatedQuestions[questionIndex].options[optionIndex],
        text: value,
      };
    } else {
      updatedQuestions[questionIndex].options[optionIndex] = {
        ...updatedQuestions[questionIndex].options[optionIndex],
        text: value,
      };
    }

    onChange({
      ...test,
      questions: updatedQuestions,
    });
  };

  const handleOptionCorrectChange = (questionIndex, optionIndex, isCorrect) => {
    const updatedQuestions = [...test.questions];
    const question = updatedQuestions[questionIndex];
    const options = [...question.options];

    if (question.type === 'single-choice' && isCorrect) {
      options.forEach((option, idx) => {
        option.isCorrect = idx === optionIndex;
      });
    } else {
      options[optionIndex].isCorrect = isCorrect;
    }

    updatedQuestions[questionIndex].options = options;
    onChange({
      ...test,
      questions: updatedQuestions,
    });
  };

  const handlePairIdChange = (questionIndex, optionIndex, pairId) => {
    const updatedQuestions = [...test.questions];
    updatedQuestions[questionIndex].options[optionIndex] = {
      ...updatedQuestions[questionIndex].options[optionIndex],
      pairId,
    };

    onChange({
      ...test,
      questions: updatedQuestions,
    });
  };

  return (
    <Card sx={{ mb: 4, p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" component="h3" fontWeight={600}>
          {t('tests:editor.testNumber', {
            number: testIndex + 1,
            title: test.title || t('tests:editor.newTest'),
          })}
        </Typography>
        {onRemove && (
          <IconButton color="error" size="small" onClick={onRemove}>
            <DeleteIcon />
          </IconButton>
        )}
      </Box>

      <Typography variant="h6" sx={{ mb: 3, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        {t('tests:editor.testDetails')}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            required
            label={t('tests:editor.testTitle')}
            name="title"
            value={test.title || ''}
            onChange={handleTestChange}
            placeholder={t('tests:editor.testTitlePlaceholder')}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t('tests:editor.timeLimit')}
            name="timeLimit"
            type="number"
            value={test.timeLimit || 15}
            onChange={handleTestChange}
            placeholder={t('tests:editor.timeLimitPlaceholder')}
            inputProps={{ min: 1 }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={t('tests:editor.passingScore')}
            name="passingScore"
            type="number"
            value={test.passingScore || 70}
            onChange={handleTestChange}
            placeholder={t('tests:editor.passingScorePlaceholder')}
            inputProps={{ min: 0, max: 100 }}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <TextField
          fullWidth
          label={t('tests:editor.testDescription')}
          name="description"
          value={test.description || ''}
          onChange={handleTestChange}
          multiline
          rows={3}
          placeholder={t('tests:editor.testDescriptionPlaceholder')}
        />
      </Box>

      <Typography variant="h6" sx={{ mt: 4, mb: 3, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        {t('tests:editor.questions')}
      </Typography>

      {test.questions?.map((question, questionIndex) => (
        <Card 
          key={questionIndex} 
          sx={{ 
            mb: 3,
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              {t('tests:editor.questionNumber', {
                number: questionIndex + 1,
              })}
            </Typography>
            <IconButton
              color="error"
              size="small"
              onClick={() => removeQuestion(questionIndex)}
              disabled={test.questions.length <= 1}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              {t('tests:editor.questionText')}
            </Typography>
            <TextField
              fullWidth
              required
              name="question"
              value={question.question || ''}
              onChange={(e) => handleQuestionChange(questionIndex, e)}
              placeholder={t('tests:editor.questionTextPlaceholder')}
              multiline
              rows={3}
              size="small"
            />
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={7}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                {t('tests:editor.answerType')}
              </Typography>
              <Select
                name="type"
                value={question.type || 'single-choice'}
                onChange={(e) => handleQuestionTypeChange(questionIndex, e)}
                size="small"
              >
                <MenuItem value="single-choice">
                  {t('tests:editor.questionTypes.singleChoice')}
                </MenuItem>
                <MenuItem value="multiple-choice">
                  {t('tests:editor.questionTypes.multipleChoice')}
                </MenuItem>
                <MenuItem value="true-false">
                  {t('tests:editor.questionTypes.trueFalse')}
                </MenuItem>
                <MenuItem value="text">
                  {t('tests:editor.questionTypes.text')}
                </MenuItem>
                <MenuItem value="memory">
                  {t('tests:editor.questionTypes.memory')}
                </MenuItem>
                <MenuItem value="open-answer">
                  {t('tests:editor.questionTypes.openAnswer')}
                </MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} sm={5}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                {t('tests:editor.questionPoints')}
              </Typography>
              <TextField
                fullWidth
                name="points"
                type="number"
                value={question.points || 1}
                onChange={(e) => handleQuestionChange(questionIndex, e)}
                placeholder={t('tests:editor.pointsPlaceholder')}
                inputProps={{ min: 1 }}
                size="small"
              />
            </Grid>
          </Grid>

            {/* Опции для выбора ответа */}
            {(question.type === 'single-choice' ||
              question.type === 'multiple-choice') && (
              <Box>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  {t('tests:editor.options')}
                </Typography>
                {question.options?.map((option, optionIndex) => (
                  <Box
                    key={`option-${optionIndex}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    {question.type === 'single-choice' ? (
                      <Radio
                        checked={option.isCorrect}
                        onChange={() =>
                          handleOptionCorrectChange(
                            questionIndex,
                            optionIndex,
                            !option.isCorrect
                          )
                        }
                        size="small"
                      />
                    ) : (
                      <Checkbox
                        checked={option.isCorrect}
                        onChange={(e) =>
                          handleOptionCorrectChange(
                            questionIndex,
                            optionIndex,
                            e.target.checked
                          )
                        }
                        size="small"
                      />
                    )}

                    <TextField
                      name="text"
                      value={option.text || ''}
                      onChange={(e) =>
                        handleOptionChange(questionIndex, optionIndex, e)
                      }
                      placeholder={t('tests:editor.optionPlaceholder', { number: optionIndex + 1 })}
                      fullWidth
                      variant="outlined"
                      size="small"
                    />

                    <IconButton
                      size="small"
                      onClick={() => removeOption(questionIndex, optionIndex)}
                      disabled={question.options.length <= 2}
                      sx={{ flexShrink: 0 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => addOption(questionIndex)}
                  >
                    {t('tests:editor.addOption')}
                  </Button>
                </Box>
              </Box>
            )}

            {/* Memory-game options */}
            {question.type === 'memory' && (
              <Box>
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                  <FormLabel component="legend">
                    {t('tests:editor.memoryCards')}
                  </FormLabel>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {question.options?.map((option, optionIndex) => (
                      <Grid item xs={12} sm={6} key={`memory-${optionIndex}`}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TextField
                            name="text"
                            value={option.text || ''}
                            onChange={(e) =>
                              handleOptionChange(questionIndex, optionIndex, e)
                            }
                            placeholder={t(
                              'tests:editor.cardPlaceholder',
                              {
                                number: optionIndex + 1,
                              }
                            )}
                            fullWidth
                            variant="outlined"
                            size="small"
                            sx={{ mr: 1 }}
                          />

                          <TextField
                            name="pairId"
                            value={option.pairId || ''}
                            onChange={(e) =>
                              handlePairIdChange(
                                questionIndex,
                                optionIndex,
                                e.target.value
                              )
                            }
                            placeholder={t('tests:editor.pairId')}
                            size="small"
                            sx={{ width: '100px', mr: 1 }}
                          />

                          <IconButton
                            size="small"
                            onClick={() =>
                              removeOption(questionIndex, optionIndex)
                            }
                            disabled={question.options.length <= 4}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <Button
                    variant="text"
                    startIcon={<AddIcon />}
                    onClick={() => addOption(questionIndex)}
                    sx={{ mt: 1 }}
                  >
                    {t('tests:editor.addCards')}
                  </Button>
                </FormControl>
              </Box>
            )}

            {/* True/False question */}
            {question.type === 'true-false' && (
              <Box>
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    {t('tests:editor.correctAnswer')}
                  </FormLabel>
                  <RadioGroup
                    row
                    name="correctAnswer"
                    value={question.correctAnswer || 'true'}
                    onChange={(e) => handleQuestionChange(questionIndex, e)}
                  >
                    <FormControlLabel
                      value="true"
                      control={<Radio />}
                      label={t(
                        'tests:editor.trueFalseOptions.true'
                      )}
                    />
                    <FormControlLabel
                      value="false"
                      control={<Radio />}
                      label={t(
                        'tests:editor.trueFalseOptions.false'
                      )}
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            )}

            {/* Text answer question */}
            {question.type === 'text' && (
              <Box>
                <TextField
                  label={t('tests:editor.textAnswer.label')}
                  name="correctAnswer"
                  value={question.correctAnswer || ''}
                  onChange={(e) => handleQuestionChange(questionIndex, e)}
                  fullWidth
                  helperText={t('tests:editor.textAnswer.helper')}
                />
              </Box>
            )}

            {/* Open answer question (AI-reviewed) */}
            {question.type === 'open-answer' && (
              <Box>
                <TextField
                  label={t('tests:editor.openAnswer.label')}
                  name="correctAnswer"
                  value={question.correctAnswer || ''}
                  onChange={(e) => handleQuestionChange(questionIndex, e)}
                  fullWidth
                  multiline
                  rows={4}
                  helperText={t('tests:editor.openAnswer.helper')}
                />
              </Box>
            )}
        </Card>
      ))}

      <Box sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addQuestion}
          size="large"
        >
          {t('tests:editor.addQuestion')}
        </Button>
      </Box>
    </Card>
  );
};

export default TestEditor;
